import React, { RefObject, useEffect, useRef, useState } from 'react';
import { Checkbox } from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import LanguageOutlinedIcon from '@mui/icons-material/LanguageOutlined';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import PlaceOutlinedIcon from '@mui/icons-material/PlaceOutlined';
import SearchIcon from '@mui/icons-material/Search';

import {
  BulkVerificationCandidate,
  BulkVerificationCandidatePage,
  DetailsOnFile,
  loadBulkVerificationCandidates,
  MAX_SELECTED_ENTITIES,
  PICKER_PAGE_SIZE,
} from './bulkVerificationCandidates';
import { formatCount, plural } from './bulkVerificationFormat';

const SEARCH_DEBOUNCE_MS = 300;

/** Fixed in CSS so a page of rows fills the table's room exactly. */
const ROW_HEIGHT = 56;
const HEAD_HEIGHT = 40;
const MIN_PAGE_SIZE = 3;
/** Room for borders' rounding, so a full page never tips into a scrollbar. */
const SLACK = 2;
const RESIZE_SETTLE_MS = 150;

const canMeasure = () => typeof ResizeObserver !== 'undefined';

/**
 * As many rows as fit the element's height, kept current as it resizes.
 * Null until measured; the default page size where layout can't be measured.
 */
const usePageSizeToFit = (ref: RefObject<HTMLElement>): number | null => {
  const [pageSize, setPageSize] = useState<number | null>(() =>
    canMeasure() ? null : PICKER_PAGE_SIZE,
  );
  useEffect(() => {
    const element = ref.current;
    if (!element || !canMeasure()) return undefined;
    const measure = () => {
      const height = element.clientHeight;
      setPageSize((current) =>
        height > 0
          ? Math.max(
              MIN_PAGE_SIZE,
              Math.floor((height - HEAD_HEIGHT - SLACK) / ROW_HEIGHT),
            )
          : current ?? PICKER_PAGE_SIZE,
      );
    };
    measure();
    let timer: ReturnType<typeof setTimeout> | undefined = undefined;
    const observer = new ResizeObserver(() => {
      clearTimeout(timer);
      timer = setTimeout(measure, RESIZE_SETTLE_MS);
    });
    observer.observe(element);
    return () => {
      observer.disconnect();
      clearTimeout(timer);
    };
  }, [ref]);
  return pageSize;
};

/** The entity tables' checkbox (Entity Hub results, Monitor hits). */
const checkboxSx = { color: 'var(--text-secondary)', padding: 0 };

interface BulkVerificationPickerProps {
  databaseId: string;
  selectedIds: ReadonlySet<string>;
  onSelectedIdsChange: (next: ReadonlySet<string>) => void;
}

const DETAIL_ICONS: Array<{
  key: keyof DetailsOnFile;
  label: string;
  Icon: typeof LanguageOutlinedIcon;
}> = [
  { key: 'website', label: 'Website', Icon: LanguageOutlinedIcon },
  { key: 'email', label: 'Email', Icon: MailOutlineIcon },
  { key: 'phone', label: 'Phone', Icon: PhoneOutlinedIcon },
  { key: 'address', label: 'Address', Icon: PlaceOutlinedIcon },
];

const DetailsCell: React.FC<{ candidate: BulkVerificationCandidate }> = ({
  candidate,
}) => {
  if (candidate.nothingToVerify) {
    return <span className='bdb-bulk-verify-tag'>Nothing to verify</span>;
  }
  return (
    <span className='bdb-bulk-verify-details'>
      {DETAIL_ICONS.map(({ key, label, Icon }) => {
        const onFile = candidate.details[key];
        return (
          <Icon
            key={key}
            role='img'
            aria-hidden={false}
            aria-label={
              onFile ? `${label} on file` : `No ${label.toLowerCase()}`
            }
            className={onFile ? undefined : 'bdb-bulk-verify-detail-off'}
            sx={{ fontSize: 18 }}
          />
        );
      })}
    </span>
  );
};

const SkeletonRows: React.FC<{ count: number }> = ({ count }) => (
  <>
    {Array.from({ length: count }, (_, i) => i).map((row) => (
      <tr
        key={row}
        className='bdb-bulk-verify-picker-skeleton'
        aria-hidden='true'
      >
        <td className='bdb-bulk-verify-picker-check'>
          <span />
        </td>
        <td>
          <span />
        </td>
        <td>
          <span />
        </td>
      </tr>
    ))}
  </>
);

const PageCheckbox: React.FC<{
  checked: boolean;
  indeterminate: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}> = ({ checked, indeterminate, disabled, onChange }) => {
  const ref = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate;
  }, [indeterminate]);
  return (
    <Checkbox
      inputRef={ref}
      size='small'
      inputProps={{ 'aria-label': 'Select page' }}
      checked={checked}
      indeterminate={indeterminate}
      disabled={disabled}
      onChange={(event) => onChange(event.target.checked)}
      sx={checkboxSx}
    />
  );
};

/** Choose the Database Entities a run covers, across pages and searches. */
const BulkVerificationPicker: React.FC<BulkVerificationPickerProps> = ({
  databaseId,
  selectedIds,
  onSelectedIdsChange,
}) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  // The first row shown, so a resize keeps the reader's place.
  const [firstIndex, setFirstIndex] = useState(0);
  const [result, setResult] = useState<BulkVerificationCandidatePage | null>(
    null,
  );
  const viewportRef = useRef<HTMLDivElement>(null);
  const pageSize = usePageSizeToFit(viewportRef);
  const page = pageSize ? Math.floor(firstIndex / pageSize) + 1 : 1;

  useEffect(() => {
    if (searchInput === search) return undefined;
    const timer = setTimeout(() => {
      setSearch(searchInput);
      setFirstIndex(0);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput, search]);

  useEffect(() => {
    if (!pageSize) return undefined;
    let cancelled = false;
    setResult(null);
    loadBulkVerificationCandidates(databaseId, {
      search: search,
      page: page,
      pageSize: pageSize,
    }).then(
      (loaded) => {
        if (!cancelled) setResult(loaded);
      },
      () => {
        if (!cancelled) {
          setResult({ candidates: [], totalPages: 1 });
        }
      },
    );
    return () => {
      cancelled = true;
    };
  }, [databaseId, search, page, pageSize]);

  const rows = result?.candidates ?? [];
  const totalPages = result?.totalPages ?? 1;
  const onPage = rows.filter((c) => selectedIds.has(c.databaseEntityId)).length;
  const count = selectedIds.size;
  const overCap = count > MAX_SELECTED_ENTITIES;

  const withPage = (selected: boolean) => {
    const next = new Set(selectedIds);
    rows.forEach((c) =>
      selected ? next.add(c.databaseEntityId) : next.delete(c.databaseEntityId),
    );
    onSelectedIdsChange(next);
  };

  const toggle = (id: string, selected: boolean) => {
    const next = new Set(selectedIds);
    if (selected) next.add(id);
    else next.delete(id);
    onSelectedIdsChange(next);
  };

  return (
    <div className='bdb-bulk-verify-picker'>
      <div className='bdb-bulk-verify-picker-bar'>
        <div className='screening-input-field-container bdb-bulk-verify-picker-search'>
          <SearchIcon sx={{ fontSize: 18 }} aria-hidden='true' />
          <input
            type='search'
            className='screening-input-field-content'
            placeholder='Search entities'
            aria-label='Search entities'
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
          />
        </div>
        <button
          type='button'
          className='btn-outline bdb-bulk-verify-btn-sm'
          disabled={rows.length === 0 || onPage === rows.length}
          onClick={() => withPage(true)}
        >
          Select page
        </button>
        <button
          type='button'
          className='btn-outline bdb-bulk-verify-btn-sm'
          disabled={onPage === 0}
          onClick={() => withPage(false)}
        >
          Clear page
        </button>
        <button
          type='button'
          className='bdb-bulk-verify-link'
          disabled={count === 0}
          onClick={() => onSelectedIdsChange(new Set())}
        >
          Clear all
        </button>
      </div>

      <div className='bdb-bulk-verify-picker-viewport' ref={viewportRef}>
        {result && rows.length === 0 ? (
          <div className='bdb-bulk-verify-picker-empty'>
            {search ? (
              <>
                <span>No entities match &ldquo;{search}&rdquo;.</span>
                <button
                  type='button'
                  className='bdb-bulk-verify-link'
                  onClick={() => setSearchInput('')}
                >
                  Clear search
                </button>
              </>
            ) : (
              <span>No entities in this database.</span>
            )}
          </div>
        ) : (
          <table
            className='bdb-bulk-verify-picker-table'
            aria-label='Entities to verify'
            aria-busy={!result}
          >
            <thead>
              <tr>
                <th className='bdb-bulk-verify-picker-check'>
                  <PageCheckbox
                    checked={rows.length > 0 && onPage === rows.length}
                    indeterminate={onPage > 0 && onPage < rows.length}
                    disabled={rows.length === 0}
                    onChange={withPage}
                  />
                </th>
                <th>Entity</th>
                <th>Details on file</th>
              </tr>
            </thead>
            <tbody>
              {!result && <SkeletonRows count={pageSize ?? PICKER_PAGE_SIZE} />}
              {rows.map((candidate) => {
                const selected = selectedIds.has(candidate.databaseEntityId);
                return (
                  <tr
                    key={candidate.databaseEntityId}
                    className={selected ? 'is-selected' : undefined}
                  >
                    <td className='bdb-bulk-verify-picker-check'>
                      <Checkbox
                        size='small'
                        inputProps={{
                          'aria-label': `Select ${candidate.name}`,
                        }}
                        checked={selected}
                        onChange={(event) =>
                          toggle(
                            candidate.databaseEntityId,
                            event.target.checked,
                          )
                        }
                        sx={checkboxSx}
                      />
                    </td>
                    <td>
                      <span className='bdb-bulk-verify-picker-name'>
                        {candidate.name}
                      </span>
                      {candidate.country && (
                        <span className='bdb-bulk-verify-picker-meta'>
                          {candidate.country}
                        </span>
                      )}
                    </td>
                    <td>
                      <DetailsCell candidate={candidate} />
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      {!(result && rows.length === 0) && (
        <div className='bdb-bulk-verify-picker-pager bdb-bulk-verify-num'>
          <span>
            Page {formatCount(page)} of {formatCount(totalPages)}
          </span>
          <button
            type='button'
            className='bdb-bulk-verify-picker-page'
            aria-label='Previous page'
            disabled={page <= 1}
            onClick={() =>
              setFirstIndex((page - 2) * (pageSize ?? PICKER_PAGE_SIZE))
            }
          >
            <ChevronLeftIcon sx={{ fontSize: 18 }} aria-hidden='true' />
          </button>
          <button
            type='button'
            className='bdb-bulk-verify-picker-page'
            aria-label='Next page'
            disabled={page >= totalPages}
            onClick={() => setFirstIndex(page * (pageSize ?? PICKER_PAGE_SIZE))}
          >
            <ChevronRightIcon sx={{ fontSize: 18 }} aria-hidden='true' />
          </button>
        </div>
      )}

      <div className='bdb-bulk-verify-picker-summary bdb-bulk-verify-num'>
        <span className='bdb-bulk-verify-picker-count'>
          {`${formatCount(count)} ${plural(
            count,
            'entity',
            'entities',
          )} selected`}
        </span>
        {overCap && (
          <span className='bdb-bulk-verify-picker-warn' role='alert'>
            That&apos;s over the {formatCount(MAX_SELECTED_ENTITIES)} limit. Use
            &ldquo;All entities&rdquo; or narrow the selection.
          </span>
        )}
      </div>
    </div>
  );
};

export default BulkVerificationPicker;
